import "./_header-action-bar.scss";
import React, {useEffect, useState} from "react";
import {Button, Dropdown, DropdownItem, Icon} from "../../../components";
import {useAppSelector} from "../../../store/hooks";
import {
    estateToggleSelector,
    getSelectedFlowchart,
    selectRevTrust,
    setEstateTaxDueTypes,
    setEstateToggle,
    setViewMode
} from "../../slices/EstateFlowChartSlice";
import {
    DistributionType,
    EstateFlowchartState,
    EstateItemObject,
    EstateToggle,
    ModalSource,
    RevocableTrust
} from "../../model/EstateFlowchartTypes";
import {LEGEND_COLOR_CONSTANTS} from "../../constants/LabelConstants";
import AddFlowchartModal from "../FlowChartModals/AddFlowchartModal";
import {EstateItemType} from "../../forms/EstateItemType";
import {Checkbox, DropdownGroup} from "xps-react";
import {EstateCardViewModeType, EstateTaxDueType} from "../EstateCard/EstateCard";
import {useDispatch} from "react-redux";
import {NO_OP} from "../../../constants/common";
import {IrrevocableTrust} from "../../model/IrrevocableTrust";
import {WillSummary} from "../../model/WillSummary";
import {hasWillSummary} from "../../utils/DefUtils";
import useProfileEditableState from "../../../hooks/useProfileEditableState";
import {selectReleaseToggles} from "../../../ReleaseToggles/releaseTogglesSlice";
import {useAnalyticsTrackers} from "../../../utils/useAnalyticsTrackers";
import {Miscellaneous} from "src/EstateFlowchart/model/Miscellaneous";

type HeaderActionBarProps = {
    onEdit: (estateItemObj: EstateItemObject) => void;
    flowcharts?: EstateFlowchartState[],
    selectedFlowchartId?: string,
    estateToggleLocal?: string
}

type FlowchartLegend = {
    legendColor: string;
    label: string;
}

const HeaderActionBar: React.FC<HeaderActionBarProps> = ({
                                                             onEdit,
                                                             flowcharts,
                                                             selectedFlowchartId,
                                                             estateToggleLocal
                                                         }: HeaderActionBarProps) => {
    const [showAddEstateItemModal, setShowAddEstateItemModal] = useState<boolean>(false);
    const [estateItemObjectList, setEstateItemObjectList] = useState<EstateItemObject[]>([] as EstateItemObject[]);
    const [legends, setLegends] = useState<FlowchartLegend[]>([] as FlowchartLegend[])
    const [scrollForEdit, setScrollForEdit] = useState<string>("auto");
    const releaseToggles = useAppSelector(selectReleaseToggles);
    const estateToggle = useAppSelector(estateToggleSelector);
    const {page} = useAnalyticsTrackers();

    const dispatch = useDispatch();
    let selectedFlowchart = useAppSelector(getSelectedFlowchart);

    if (selectedFlowchartId && flowcharts && flowcharts.length > 0) {
        selectedFlowchart = flowcharts.find(fc => fc.flowchartId === selectedFlowchartId);
    }
    const revTrust = useAppSelector(selectRevTrust);
    const estateToggleOptions = [
        {label: "In Estate", value: EstateToggle.IN_ESTATE},
        {label: "Full Estate", value: EstateToggle.FULL_ESTATE},
        {label: "Out of Estate", value: EstateToggle.OUT_OF_ESTATE}
    ];

    useEffect(() => {
        const estateItemsData = collectEstateItems(selectedFlowchart, releaseToggles);
        setEstateItemObjectList(estateItemsData)
    }, [selectedFlowchart, releaseToggles]);

    useEffect(() => {
        setLegends([]);
        if (!selectedFlowchart) {
            return;
        }
        let newLegends: FlowchartLegend[] = [];
        switch (estateToggle) {
            case EstateToggle.IN_ESTATE:
                newLegends = [
                    ...getRevTrustLegends(revTrust),
                    ...getWillLegends(selectedFlowchart.willSummary),
                    ...getWildcardLegends(selectedFlowchart?.wildcardTrusts, EstateToggle.IN_ESTATE)
                ];
                break;
            case EstateToggle.FULL_ESTATE:
                newLegends = [
                    ...getRevTrustLegends(revTrust),
                    ...getIrrevocableTrustLegends(selectedFlowchart.irrevocableTrusts),
                    ...getWillLegends(selectedFlowchart.willSummary),
                    ...getWildcardLegends(selectedFlowchart?.wildcardTrusts, EstateToggle.FULL_ESTATE)
                ];
                break;
            case EstateToggle.OUT_OF_ESTATE:
                newLegends = [
                    ...(selectedFlowchart?.irrevocableTrusts ? getIrrevocableTrustLegends(selectedFlowchart.irrevocableTrusts) : []),
                    ...getWildcardLegends(selectedFlowchart?.wildcardTrusts, EstateToggle.OUT_OF_ESTATE)
                ];
                break;
        }

        // Remove duplicates based on label and sort by label
        newLegends = Array.from(new Set(
            new Map(newLegends.map(legend => [legend.label, legend])).values()
        )).sort((a, b) => a.label.localeCompare(b.label));

        // Ensure 'Will' is always at start of the legends
        const will = newLegends.find(legend => legend.label === 'Will');
        if (will) {
            const willIndex = newLegends.findIndex(legend => legend.label === 'Will');
            newLegends.splice(willIndex, 1);
            newLegends.unshift(will);
        }

        //Ensure 'Misc' is always at the end of the legends
        const miscIndex = newLegends.findIndex(legend => legend.label.startsWith('Miscellaneous'));
        if (miscIndex !== -1) {
            const miscLegend = newLegends.splice(miscIndex, 1)[0];
            newLegends.push(miscLegend);
        }
        setLegends(newLegends);

        if (releaseToggles?.enableEstateFlowchartMVPRelease) {
            let dropdownItems = collectEstateItems(selectedFlowchart, releaseToggles);
            if (dropdownItems.length > 7) {
                let height = dropdownItems.length * 34 < 238 ? (dropdownItems.length * 34) + 20 : 238;
                setScrollForEdit(height + "px");
            } else {
                setScrollForEdit("auto");
            }
        }
    }, [estateToggle, selectedFlowchart, releaseToggles]);

    const getRevTrustLegends = (revTrust: RevocableTrust | undefined | null): FlowchartLegend[] => {
        if (!revTrust) {
            return [];
        }

        let legends: FlowchartLegend[] = [];
        if (revTrust?.beneficiaries) {
            const additionalDistributionTypes = releaseToggles?.enableAdditionalDistributionLayer
                ? (revTrust.beneficiaries.additionalDistributions?.distributions ?? []).map(distribution => distribution.distributionType)
                : [];
            const allDistributionTypes = [
                ...revTrust.beneficiaries.firstDeathDistributions.map(distribution => distribution.distributionType),
                ...revTrust.beneficiaries.remainderDistributions.map(distribution => distribution.distributionType),
                ...additionalDistributionTypes,
            ];
            legends = groupBeneficiaryDistributions(allDistributionTypes);
        }

        legends.push({legendColor: LEGEND_COLOR_CONSTANTS.TRUST, label: "Trust"}); // Ensure Trust is always included when no distributions are available
        return legends;
    }

    const getIrrevocableTrustLegends = (irrevocableTrusts: IrrevocableTrust[] | undefined | null): FlowchartLegend[] => {
        if (!irrevocableTrusts || irrevocableTrusts.length === 0) {
            return [];
        }

        let legends: FlowchartLegend[] = [];

        irrevocableTrusts?.forEach((irrevocableTrust) => {
            if (irrevocableTrust?.firstDeathBeneficiaries) {
                const allDistributionTypes = [
                    ...irrevocableTrust.firstDeathBeneficiaries.firstDeathDistributions?.map(distribution => distribution.distributionType),
                ];
                legends = Array.from(new Set([...legends, ...groupBeneficiaryDistributions(allDistributionTypes)]));
            }
        })

        legends.push({legendColor: LEGEND_COLOR_CONSTANTS.TRUST, label: "Trust"}); // Ensure Trust is always included when no distributions are available
        return legends;
    }

    const getWillLegends = (wills: WillSummary | undefined): FlowchartLegend[] => {
        if (!hasWillSummary(wills).hasWills) {
            return [];
        }

        return [{legendColor: LEGEND_COLOR_CONSTANTS.WILL, label: "Will"}];
    }

    function getWildcardLegends(wildcardTrusts: Miscellaneous[] | undefined, estateToggle: EstateToggle): FlowchartLegend[] {
        if (!wildcardTrusts || !releaseToggles?.enableWildCardInEstateFlowchart) return [];
        let filteredWildcards: Miscellaneous[] = [];
        if (estateToggle === EstateToggle.FULL_ESTATE) {
            filteredWildcards = wildcardTrusts;
        } else if (estateToggle === EstateToggle.IN_ESTATE) {
            filteredWildcards = wildcardTrusts.filter(misc => misc.estateToggleType === EstateToggle.IN_ESTATE);
        } else if (estateToggle === EstateToggle.OUT_OF_ESTATE) {
            filteredWildcards = wildcardTrusts.filter(misc => misc.estateToggleType === EstateToggle.OUT_OF_ESTATE);
        }

        const uniqueTreatments = Array.from(new Set(filteredWildcards.map(misc => misc.visualCardRepresentation?.toUpperCase())));
        const legends: FlowchartLegend[] = [];
        uniqueTreatments.forEach(treatment => {
            if (treatment === "OUTRIGHT") {
                legends.push({ legendColor: LEGEND_COLOR_CONSTANTS.OUTRIGHT, label: "Outright" });
            } else if (treatment === "CHARITY") {
                legends.push({ legendColor: LEGEND_COLOR_CONSTANTS.CHARITY, label: "Charity" });
            } else if (treatment === "TRUST") {
                legends.push({ legendColor: LEGEND_COLOR_CONSTANTS.TRUST, label: "Trust" });
            } else if (treatment === "MISCELLANEOUS") {
                legends.push({ legendColor: LEGEND_COLOR_CONSTANTS.MISCELLANEOUS, label: "Miscellaneous" });
            }
        });
        return legends;
    }

    const groupBeneficiaryDistributions = (allDistributionTypes: DistributionType[]) => {
        let legends: FlowchartLegend[] = [];
        const uniqueDistributionTypes = Array.from(new Set(allDistributionTypes));
        uniqueDistributionTypes.forEach((distributionType) => {
            switch (distributionType) {
                case "CHARITY":
                    legends.push({legendColor: LEGEND_COLOR_CONSTANTS.CHARITY, label: "Charity"});
                    break;
                case "OUTRIGHT":
                    legends.push({legendColor: LEGEND_COLOR_CONSTANTS.OUTRIGHT, label: "Outright"});
                    break;
                default:
                    legends.push({legendColor: LEGEND_COLOR_CONSTANTS.TRUST, label: "Trust"}); // Default to trust
            }
        });
        return legends;
    }

    const editClickHandler = () => {
        onEdit(estateItemObjectList[0]);
    }

    const collectEstateItems = (flowchart: EstateFlowchartState | undefined, releaseToggles: any) => {
        if (!flowchart) {
            return [];
        }
        const estateItemObjectList: EstateItemObject[] = [];
        if (flowchart?.revocableTrust?.nickName) {
            estateItemObjectList.push({
                type: EstateItemType.REVOCABLE_TRUST,
                id: flowchart.revocableTrust.trustId,
                nickName: flowchart?.revocableTrust?.nickName
            });
        }
        flowchart?.irrevocableTrusts?.forEach((irrevocable) => {
            estateItemObjectList.push({
                type: EstateItemType.IRREVOCABLE_TRUST,
                id: irrevocable.trustId,
                nickName: irrevocable.nickName
            });
        });

        const flowchartWithWildcards = flowchart as EstateFlowchartState & { wildcardTrusts?: Miscellaneous[] };
        if (releaseToggles?.enableWildCardInEstateFlowchart && Array.isArray(flowchartWithWildcards.wildcardTrusts)) {
            (flowchartWithWildcards.wildcardTrusts as Miscellaneous[]).forEach((wildCard: Miscellaneous) => {
                estateItemObjectList.push({
                    type: EstateItemType.MISCELLANEOUS,
                    id: wildCard.trustId,
                    nickName: wildCard.name
                });
            });
        }

        const willSummary = flowchart?.willSummary;
        if (willSummary?.primaryWill) {
            estateItemObjectList.push({
                type: EstateItemType.WILL,
                id: willSummary.primaryWill.trustId,
                nickName: willSummary.primaryWill.nickName
            });
        }
        if (willSummary?.partnerWill) {
            estateItemObjectList.push({
                type: EstateItemType.WILL,
                id: willSummary.partnerWill.trustId,
                nickName: willSummary.partnerWill.nickName
            });
        }

        estateItemObjectList.sort(
            (a, b) => a.nickName.localeCompare(b.nickName, undefined, {sensitivity: 'base'})
        );
        return estateItemObjectList;
    }

    const createDropdownItems = () => {
        const dropdownItems: JSX.Element[] = [];
        estateItemObjectList.forEach((estateItemObj, index) => {
            dropdownItems.push(<DropdownItem key={estateItemObj.nickName + index} itemText={estateItemObj.nickName}
                                             className={"edit-dropdown-items"}
                                             value={estateItemObj.nickName} onClick={() => onEdit(estateItemObj)}/>);
        })
        return dropdownItems;
    }

    const handleViewModeRadioButtonClick = (event: React.MouseEvent<HTMLElement>,
                                            selectedViewMode: EstateCardViewModeType) => {
        dispatch(setViewMode(selectedViewMode));
        if(event) event.stopPropagation();
    }

    const {isProfileWithProposalsOrArchived} = useProfileEditableState();

    const handleEstateTaxToggleClick = (e: React.MouseEvent<HTMLDivElement>, estateTaxDueType: EstateTaxDueType) => {
        e.preventDefault();
        e.stopPropagation();
        if (getDisabled(estateTaxDueType)) return;

        const existingEstateTaxDueTypes = selectedFlowchart?.estateTaxDueTypes || [];
        const isSelected = existingEstateTaxDueTypes.includes(estateTaxDueType);

        const updatedEstateTaxDueTypes = isSelected
            ? existingEstateTaxDueTypes.filter(type => type !== estateTaxDueType)
            : [...existingEstateTaxDueTypes, estateTaxDueType];

        dispatch(setEstateTaxDueTypes(updatedEstateTaxDueTypes));
    }

    function getDisabled(estateTaxDueType: EstateTaxDueType) {
        if (!selectedFlowchart?.revocableTrust) return true;

        const {beneficiaries} = selectedFlowchart.revocableTrust;

        if (estateTaxDueType === EstateTaxDueType.FIRST_DEATH) {
            return !beneficiaries?.firstDeathDistributions?.length;
        }

        return estateTaxDueType === EstateTaxDueType.SECOND_DEATH &&
            !beneficiaries?.remainderDistributions?.length;
    }

    function getChecked(estateTaxDueType: EstateTaxDueType) {
        return !getDisabled(estateTaxDueType) && !!selectedFlowchart?.estateTaxDueTypes?.includes(estateTaxDueType);
    }

    const renderLegends = (estateToggleLocal: string, selectedFlowchart: EstateFlowchartState) => {
        let newLegends: FlowchartLegend[] = [];
        switch (estateToggleLocal) {
            case "IN_ESTATE":
                newLegends = [...getRevTrustLegends(selectedFlowchart.revocableTrust),
                    ...getWillLegends(selectedFlowchart.willSummary),
                    ...getWildcardLegends(selectedFlowchart.wildcardTrusts, EstateToggle.IN_ESTATE)];
                break;
            case "FULL_ESTATE":
                newLegends = [...getRevTrustLegends(selectedFlowchart.revocableTrust),
                    ...getIrrevocableTrustLegends(selectedFlowchart.irrevocableTrusts),
                    ...getWillLegends(selectedFlowchart.willSummary),
                    ...getWildcardLegends(selectedFlowchart.wildcardTrusts, EstateToggle.FULL_ESTATE)];
                break;
            case "OUT_OF_ESTATE":
                newLegends = selectedFlowchart?.irrevocableTrusts ? [...getIrrevocableTrustLegends(selectedFlowchart.irrevocableTrusts),
                    ...getWildcardLegends(selectedFlowchart.wildcardTrusts, EstateToggle.OUT_OF_ESTATE)] : [];
                break;
        }
        newLegends = Array.from(new Set(
            new Map(newLegends.map(legend => [legend.label, legend])).values()
        )).sort((x, y) => x.label.localeCompare(y.label));

        // Ensure 'Will' is always at start of the legends
        const will = newLegends.find(legend => legend.label === 'Will');
        if (will) {
            const willIndex = newLegends.findIndex(legend => legend.label === 'Will');
            newLegends.splice(willIndex, 1);
            newLegends.unshift(will);
        }

        //Ensure 'Misc' is always at the end of the legendsExpand commentComment on line R123ResolvedCode has comments. Press enter to view.
        const miscIndex = newLegends.findIndex(legend => legend.label.startsWith('Miscellaneous'));
        if (miscIndex !== -1) {
            const miscLegend = newLegends.splice(miscIndex, 1)[0];
            newLegends.push(miscLegend);
        }
        return newLegends.map((legend) => (
            <div key={legend.label}>
                <Icon name={"media_record"} color={legend.legendColor} className={"legend-icon"}/>
                <span className={"legend-label"}>{legend.label}</span>
            </div>
        ));
    }
    const handleActionMenuBlur = () => {
        const viewMode = selectedFlowchart?.viewMode;
        const estateTaxDueTypes = selectedFlowchart?.estateTaxDueTypes || [];
        page(`DEF_Click_View_By_${viewMode}`);
        page(`DEF_Click_Estate_Tax_Due_${estateTaxDueTypes}`)
    };

    function isEstateToggleEnabled(estateItemObjectList: EstateItemObject[], wildcardTrusts: Miscellaneous[] | undefined) {
        let hasInEstate = estateItemObjectList.some(item => item.type === EstateItemType.REVOCABLE_TRUST || item.type === EstateItemType.WILL);
        let hasOutEstate = estateItemObjectList.some(item => item.type === EstateItemType.IRREVOCABLE_TRUST);
        if (releaseToggles?.enableWildCardInEstateFlowchart) {
            hasInEstate = hasInEstate || !!(wildcardTrusts && wildcardTrusts.some(misc => misc.estateToggleType === EstateToggle.IN_ESTATE));
            hasOutEstate = hasOutEstate || !!(wildcardTrusts && wildcardTrusts.some(misc => misc.estateToggleType === EstateToggle.OUT_OF_ESTATE));
        }
        return hasInEstate && hasOutEstate;
    }

    return (
        <div className={"header-action-bar"}>
            <div className="estate-toggles" data-testid="flowchart-estate-toggle">
                {
                    estateToggleOptions.map((option, idx) => {
                        let buttonOrientation = "";
                        if (idx === 0) {
                            buttonOrientation = " left";
                        } else if (idx === estateToggleOptions.length - 1) {
                            buttonOrientation = " right";
                        }
                        return <Button
                            key={option.value}
                            className={`estate-toggle-btn${buttonOrientation} ${estateToggle === option.value ? " active" : ""}`}
                            kind="secondary"
                            disabled={!isEstateToggleEnabled(estateItemObjectList, selectedFlowchart?.wildcardTrusts)}
                            size="medium"
                            onClick={() => dispatch(setEstateToggle({toggle: option.value}))}
                        >
                            {option.label}
                        </Button>
                    })
                }
            </div>

            <div className="header-group-1">
                <div className="flowchart-legends">
                    {estateToggleLocal && releaseToggles?.enableEstateFlowchartMVPRelease
                        ? renderLegends(estateToggleLocal, selectedFlowchart!)
                        : legends.map((legend) => (
                            <div key={legend.label}>
                                <Icon name={"media_record"} color={legend.legendColor} className={"legend-icon"}/>
                                <span className={"legend-label"}>{legend.label}</span>
                            </div>
                        ))
                    }
                </div>

                <div className={"action-buttons"}>
                    <Dropdown buttonKind={"secondary"}
                              panelHeight={"auto"}
                              panelWidth={260}
                              rounded={true}
                              buttonIcon={"only"}
                              buttonSize={"medium"}
                              dropdownKind={"menu"}
                              iconNameOpen={"menu_dots"}
                              iconNameClose={"menu_dots"}
                              alignRight={true}
                              onBlur={(event) => {
                                  event.stopPropagation()
                                  if (
                                      event.relatedTarget === null ||
                                      (!event.relatedTarget?.getAttribute?.("aria-label") && !event.relatedTarget?.getAttribute?.("value"))
                                  ) {
                                      handleActionMenuBlur();
                                      console.log(event.relatedTarget)
                                  }
                              }}
                    >
                        <DropdownGroup className="flowchart-header-dropdown-group" groupName="View By">
                            <DropdownItem itemText="" style={{padding: 0}} value="SIMPLE" onClick={NO_OP}>
                                <div role={"button"}
                                     tabIndex={-1}
                                     style={{padding: 8}}
                                     aria-disabled={false}
                                     onKeyDown={NO_OP}
                                     aria-label={"Simple"}
                                     className="flowchart-header-dropdown-item"
                                     onClick={(e) => handleViewModeRadioButtonClick(e, EstateCardViewModeType.SIMPLE)}
                                >
                                    <input name="view-by-group"
                                           type="radio"
                                           title={"Simple"}
                                           value="simple-view"
                                           style={{accentColor: "#3D4042"}}
                                           checked={selectedFlowchart?.viewMode === EstateCardViewModeType.SIMPLE}
                                           readOnly
                                    />
                                    <span>Simple</span>
                                </div>
                            </DropdownItem>
                            <DropdownItem itemText="" style={{padding: 0}} value="STANDARD" onClick={NO_OP}>
                                <div role={"button"}
                                     tabIndex={-1}
                                     style={{padding: 8}}
                                     aria-disabled={false}
                                     onKeyDown={NO_OP}
                                     aria-label={"Standard"}
                                     className="flowchart-header-dropdown-item"
                                     onClick={(e) => handleViewModeRadioButtonClick(e, EstateCardViewModeType.STANDARD)}>
                                    <input name="view-by-group"
                                           type="radio"
                                           value="standard-view"
                                           style={{accentColor: "#3D4042"}}
                                           checked={selectedFlowchart?.viewMode === EstateCardViewModeType.STANDARD}
                                           readOnly
                                    />
                                    <span>Standard</span>
                                </div>
                            </DropdownItem>
                        </DropdownGroup>
                        <DropdownGroup className="flowchart-header-dropdown-group" groupName="Estate Tax">
                            <DropdownItem itemText="" value="dueOnFirstDeath"
                                          className={"estate-tax-due-dropdown-item"}>
                                <div role={"button"}
                                     tabIndex={-1}
                                     onKeyDown={NO_OP}
                                     aria-label={"Due on First Death Checkbox"}
                                     className="flowchart-header-dropdown-item"
                                     onClick={(e) => handleEstateTaxToggleClick(e, EstateTaxDueType.FIRST_DEATH)}>
                                    <Checkbox name={"Due on First Death"}
                                              value="Due on First Death"
                                              checked={getChecked(EstateTaxDueType.FIRST_DEATH)}
                                              disabled={getDisabled(EstateTaxDueType.FIRST_DEATH)}
                                              readOnly
                                    />
                                </div>
                            </DropdownItem>
                            <DropdownItem itemText="" value="dueOnSecondDeath"
                                          className={"estate-tax-due-dropdown-item"}>
                                <div role={"button"}
                                     tabIndex={-1}
                                     onKeyDown={NO_OP}
                                     aria-label={"Due on Second Death Checkbox"}
                                     className="flowchart-header-dropdown-item "
                                     onClick={(e) => handleEstateTaxToggleClick(e, EstateTaxDueType.SECOND_DEATH)}>
                                    <Checkbox name={"Due on Second Death"}
                                              value="Due on Second Death"
                                              checked={getChecked(EstateTaxDueType.SECOND_DEATH)}
                                              disabled={getDisabled(EstateTaxDueType.SECOND_DEATH)}
                                              readOnly
                                    />
                                </div>
                            </DropdownItem>
                        </DropdownGroup>
                    </Dropdown>

                    <Button
                        rounded={true}
                        kind={"primary"}
                        icon={"left"}
                        iconName={"add"}
                        size={"medium"}
                        disabled={isProfileWithProposalsOrArchived}
                        tabIndex={0}
                        onClick={() => setShowAddEstateItemModal(true)}
                    >
                        Add Estate Item
                    </Button>

                    {estateItemObjectList.length > 1 ? <Dropdown
                        aria-label="Edit"
                        defaultText="EDIT"
                        buttonKind="secondary"
                        alignRight={false}
                        buttonIcon="right"
                        dropdownKind="menu"
                        iconNameClose="menu_up"
                        iconNameOpen="menu_down"
                        drop="down"
                        disabled={isProfileWithProposalsOrArchived}
                        panelWidth={255}
                        dropUp={false}
                        className={"edit-dropdown"}
                        panelHeight={scrollForEdit}
                    >
                        {createDropdownItems()}
                    </Dropdown> : <Button
                        icon="left"
                        id="edit_flowchart_button"
                        kind="secondary"
                        onClick={editClickHandler}
                        size="medium"
                        disabled={isProfileWithProposalsOrArchived}
                        tabIndex={0}
                        type="button"
                        iconName="edit"
                    >
                        Edit
                    </Button>

                    }
                </div>
            </div>
            <AddFlowchartModal isOpen={showAddEstateItemModal}
                               handleCancel={() => setShowAddEstateItemModal(false)}
                               modalSource={ModalSource.AddEstateItem}/>
        </div>);
}

export default HeaderActionBar;
